import { Box, Button, CircularProgress, Typography, alpha } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';

import { useTranslation } from '../../../hooks/useTranslation';
import { useAuth } from '../../../context/AuthContext';
import { useIntegrationsStatusContext, IntegrationsStatusProvider } from '../../../context/IntegrationsStatusContext';
import { useIntegrationService } from '../../../services/integration.service';
import { useUserService } from '../../../services/user.service';
import { useCelloService } from '../../../services/cello/cello-api-service';
import { useCelloData } from '../../../hooks/useCelloData';
import { useGoogleChatNotificationService } from '../../../services/googleChatNotification.service';
import { saveUserIntegrationsStandalone } from '../../../services/user-integrations.service';
import { Platform } from '../../../services/integration.types';
import PlatformSelectionGrid from '../../integrations/PlatformSelectionGrid';

interface IntegrationStepProps {
    onIntegrationAdded: () => void;
    onNext: () => void;
    onBack: () => void;
}

// Wrap the component with the optimized IntegrationsStatusProvider
const IntegrationStepWithProvider: React.FC<IntegrationStepProps> = (props) => {
    return (
        <IntegrationsStatusProvider isOnboarding={true}>
            <IntegrationStep {...props} />
        </IntegrationsStatusProvider>
    );
};

const IntegrationStep: React.FC<IntegrationStepProps> = ({ onIntegrationAdded, onNext, onBack }) => {
    const { t } = useTranslation();
    const integrationService = useIntegrationService();
    const { isLoading: isIntegrationsLoading } = useIntegrationsStatusContext();
    const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([]);
    const [isCompleting, setIsCompleting] = useState(false);

    // Fetch platforms
    const { data: platforms = [], isLoading: isPlatformsLoading } = useQuery({
        queryKey: ['platforms'],
        queryFn: async () => {
            const allPlatforms = await integrationService.getAllPlatforms();
            return allPlatforms.filter((platform) => !platform.legacy);
        },
        staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    });

    // Check if a platform is already selected
    const isPlatformSelected = (platform: Platform): boolean => {
        return selectedPlatforms.some((p) => p.name === platform.name);
    };

    // Handle platform selection
    const handlePlatformSelect = (platform: Platform) => {
        if (isPlatformSelected(platform)) {
            // If already selected, remove it
            setSelectedPlatforms(selectedPlatforms.filter((p) => p.name !== platform.name));
        } else {
            // Add to selected platforms
            setSelectedPlatforms([...selectedPlatforms, platform]);
        }
    };

    // Handle chip removal
    const handleRemovePlatform = (platform: Platform) => {
        setSelectedPlatforms(selectedPlatforms.filter((p) => p.name !== platform.name));
    };

    // Handle done button click - complete onboarding
    const handleNext = async () => {
        setIsCompleting(true);
        try {
            // Store selected platforms in localStorage first
            localStorage.setItem('selectedPlatforms', JSON.stringify(selectedPlatforms.map(p => p.name)));

            // Call the onIntegrationAdded function to update the parent component
            if (selectedPlatforms.length > 0) {
                onIntegrationAdded();
            }

            // Save selected platforms to the API
            const savedPlatforms = localStorage.getItem('selectedPlatforms');
            if (savedPlatforms) {
                try {
                    const platformNames = JSON.parse(savedPlatforms);
                    if (platformNames.length > 0) {
                        // Get full platform details
                        const allPlatforms = await integrationService.getAllPlatforms();

                        // Process each platform name
                        const selectedPlatformsWithDetails = platformNames.map((name: string) => {
                            // Check if it's a custom platform (name starts with 'custom-')
                            if (name.startsWith('custom-')) {
                                const displayName = name.replace('custom-', '');
                                // Create a custom platform object that matches the Platform type
                                return {
                                    name: name,
                                    display_name: displayName,
                                    logo_uri: '',
                                    platform_type: 'exchange',
                                    position: 0,
                                    legacy: false,
                                    beta: false,
                                    custom_api_uri: '',
                                    custom_csv_uri: '',
                                    isCustom: true,
                                    status: 'selected'
                                } as Platform & { isCustom: boolean };
                            } else {
                                // Find the platform in the list of all platforms
                                const platform = allPlatforms.find((platform) => platform.name === name);
                                return platform ? { ...platform, isCustom: false,  status: 'selected' } : null;
                            }
                        }).filter((platform: any): platform is (Platform & { isCustom: boolean }) => platform !== null);

                        // Save to the API
                        await saveUserIntegrationsStandalone(selectedPlatformsWithDetails);
                    }
                } catch (error) {
                    console.error('Error saving user integrations:', error);
                    // Continue with onboarding completion even if saving integrations fails
                }
            }


            // Call onNext to go to the final step
            onNext();
        } catch (error) {
            console.error('Error completing onboarding:', error);
        } finally {
            setIsCompleting(false);
        }
    };

    const isLoading = isPlatformsLoading || isIntegrationsLoading;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                height: '100%',
                width: '100%',
                maxWidth: 800,
                margin: '0 auto',
            }}
        >
            <Typography variant="h5" component="h2" fontWeight={600}>
                {t('onboarding.integration.title')}
            </Typography>

            <Typography variant="body1" color="text.secondary">
                {t('onboarding.integration.selectExchangesDescription')}
            </Typography>

            {isLoading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 200,
                    }}
                >
                    <CircularProgress size={40} color="primary" />
                </Box>
            ) : (
                <Box>
                    {/* Platform Selection Grid */}
                    <PlatformSelectionGrid
                        platforms={platforms}
                        isLoading={isLoading}
                        onPlatformSelect={handlePlatformSelect}
                        checkIfIntegrated={isPlatformSelected}
                        maxHeight={400}
                        isOnboarding={true} // Flag to indicate this is used in onboarding
                    />
                </Box>
            )}

            {/* Selected Platforms Display */}
            {selectedPlatforms.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('onboarding.integration.selectedPlatforms')}:
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1.5,
                        }}
                    >
                        {selectedPlatforms.map((platform) => (
                            <Box
                                key={platform.name}
                                onClick={() => handleRemovePlatform(platform)}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    padding: '4px 10px',
                                    borderRadius: '4px',
                                    backgroundColor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: (theme) => theme.palette.divider,
                                    boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.2s ease',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.15)',
                                        backgroundColor: (theme) => alpha(theme.palette.error.light, 0.05),
                                    }
                                }}
                            >
                                {platform.logo_uri ? (
                                    <Box
                                        component="img"
                                        src={'https://cointracking.info' + platform.logo_uri}
                                        alt={platform.display_name}
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            objectFit: 'contain'
                                        }}
                                    />
                                ) : (
                                    <Box
                                        sx={{
                                            width: 20,
                                            height: 20,
                                            borderRadius: '50%',
                                            backgroundColor: (theme) => theme.palette.primary.main,
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {platform.display_name.charAt(0).toUpperCase()}
                                    </Box>
                                )}
                                <Typography variant="body2" fontWeight={500}>
                                    {platform.display_name}
                                </Typography>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 16,
                                        height: 16,
                                        borderRadius: '50%',
                                        backgroundColor: (theme) => alpha(theme.palette.divider, 0.5),
                                        color: 'text.secondary',
                                        flexShrink: 0,
                                        fontSize: '14px',
                                        '&:hover': {
                                            backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
                                            color: 'error.main',
                                        }
                                    }}
                                >
                                    ×
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Navigation buttons */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginTop: 'auto',
                }}
            >
                <Button 
                    onClick={onBack} 
                    variant="outlined"
                    sx={{ 
                        minWidth: 100,
                        borderColor: (theme) => alpha(theme.palette.divider, 0.8),
                        color: 'text.primary',
                    }}
                >
                    {t('onboarding.integration.back')}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={selectedPlatforms.length === 0 || isCompleting}
                    sx={{
                        minWidth: 150,
                    }}
                >
                    {isCompleting ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : selectedPlatforms.length > 0
                        ? t('onboarding.integration.next')
                        : t('onboarding.integration.selectPlatforms')}
                </Button>
            </Box>
        </Box>
    );
};

export default IntegrationStepWithProvider;
